import { InfoIcon } from '@phosphor-icons/react'
import { endOfDay } from 'date-fns'
import { useState } from 'react'

import {
  type SaltoFob,
  useDeleteSaltoSpaceFob,
  useGetSaltoSpaceFobs,
  useRevokeSaltoSpaceFob,
  useUpdateSaltoSpaceFob,
} from '@/api/keyless/salto-space/get-salto-space-fobs'
import SaltoSpaceIssueFobModal from '@/components/Clients/ClientProfileView/IntegrationsSection/SaltoSpaceIssueFobModal'
import { Button } from '@/components/ui/button'
import { ChipSelect } from '@/components/ui/chip-select'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Flex } from '@/components/ui/flex'
import { Tooltip } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { popupFactory, toastFactory } from '@/factories'
import { getApiErrorMessage } from '@/utils/error'

import * as S from '../keyless-access.styled'
import { KeylessTabSkeleton } from '../keyless-access-tab-skeleton'

const EXTERNAL_FOB_TOOLTIP =
  'Physical fobs/cards created directly in Salto Space. You can update their access or revoke them here.'

const UPDATE_PREPARE_MESSAGE = (
  <>
    You are about to update a physical fob/card.
    <br />
    Place the resident's fob/card on the encoder now, and click Next to continue.
  </>
)

const REVOKE_PREPARE_MESSAGE = (
  <>
    You are about to revoke this physical fob/card from the resident.
    <br />
    Place the fob/card on the encoder now, and click Next to continue.
  </>
)

const DELETE_CONFIRM_MESSAGE =
  'Permanently removes this card from Salto Space. Use this only if the physical card is lost, stolen, or otherwise unrecoverable, unlike Revoke, a deleted card/FOB cannot be handed to another resident. This action cannot be undone. Are you sure you want to permanently delete this card?'

interface Option {
  label: string
  value: string
}

interface SaltoFobRecordProps {
  fob: SaltoFob
  buildingId: number
  clientEmail: string
  doorOptions: Option[]
  zoneOptions: Option[]
  accessLevelOptions: Option[]
}

const sameIds = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id) => b.includes(id))

const FieldLabel = ({ children }: { children: string }) => (
  <Flex gap={6} align="center">
    <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
      {children}
    </Typography>
  </Flex>
)

export const SaltoFobRecord = ({
  fob,
  buildingId,
  clientEmail,
  doorOptions,
  zoneOptions,
  accessLevelOptions,
}: SaltoFobRecordProps) => {
  const [doorIds, setDoorIds] = useState<string[]>(fob.doorIds)
  const [groupIds, setGroupIds] = useState<string[]>(fob.permissionGroupIds)
  const [accessLevelIds, setAccessLevelIds] = useState<string[]>(fob.accessLevelIds)
  const [expirationDate, setExpirationDate] = useState<Date | null>(
    fob.expirationDate ? new Date(fob.expirationDate) : null,
  )
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [showRevokeModal, setShowRevokeModal] = useState(false)

  const { mutateAsync: updateFob } = useUpdateSaltoSpaceFob(buildingId, clientEmail)
  const { mutateAsync: revokeFob } = useRevokeSaltoSpaceFob(buildingId, clientEmail)
  const { mutateAsync: deleteFob } = useDeleteSaltoSpaceFob(buildingId, clientEmail)

  const initialExpDay = fob.expirationDate ? new Date(fob.expirationDate).toDateString() : null
  const currentExpDay = expirationDate ? expirationDate.toDateString() : null
  const hasChanges =
    !sameIds(doorIds, fob.doorIds) ||
    !sameIds(groupIds, fob.permissionGroupIds) ||
    !sameIds(accessLevelIds, fob.accessLevelIds) ||
    currentExpDay !== initialExpDay

  const handleUpdate = (encoderId: string): Promise<void> =>
    updateFob({
      accessLevelIds,
      doorIds,
      encoderId,
      expirationDate: expirationDate ? endOfDay(expirationDate).toISOString() : undefined,
      fobHolderId: fob.fobHolderId,
      permissionGroupIds: groupIds,
    }).then(() => undefined)

  const handleRevoke = (encoderId: string): Promise<void> =>
    revokeFob({ encoderId, fobHolderId: fob.fobHolderId }).then(() => undefined)

  const handleDelete = async (): Promise<void> => {
    try {
      await deleteFob({ fobHolderId: fob.fobHolderId })
      toastFactory.success('FOB/Card deleted successfully')
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const openDeleteConfirm = () =>
    popupFactory.open(ConfirmationPopup, {
      children: DELETE_CONFIRM_MESSAGE,
      confirmText: 'Yes, delete',
      onConfirm: handleDelete,
      title: 'Delete the FOB/Card?',
      variant: 'warning',
    })

  return (
    <S.TabContent>
      <S.FieldRow>
        <FieldLabel>Door</FieldLabel>
        <ChipSelect
          size="small"
          options={doorOptions}
          value={doorIds}
          placeholder="Select Door..."
          onChange={setDoorIds}
        />
      </S.FieldRow>
      <S.FieldRow>
        <FieldLabel>Zone</FieldLabel>
        <ChipSelect
          size="small"
          options={zoneOptions}
          value={groupIds}
          placeholder="Select Zone..."
          onChange={setGroupIds}
        />
      </S.FieldRow>
      <S.FieldRow>
        <FieldLabel>Access Level</FieldLabel>
        <ChipSelect
          size="small"
          options={accessLevelOptions}
          value={accessLevelIds}
          placeholder="Select Access Level..."
          onChange={setAccessLevelIds}
        />
      </S.FieldRow>
      <S.FieldRow>
        <FieldLabel>Expiration Date</FieldLabel>
        <DatePickerInput
          size="small"
          placeholder="MM/DD/YYYY"
          value={expirationDate}
          minDate={new Date()}
          onChange={setExpirationDate}
          onRestore={() => setExpirationDate(null)}
        />
      </S.FieldRow>
      <Flex justify="space-between" align="center" gap={12} padding="12 16">
        <Typography size="text-small" color="black500" as="span">
          {fob.fobHolderName ? `${fob.fobHolderName} · ` : ''}
          {fob.fobId ? `ROM ${fob.fobId}` : ''}
        </Typography>
        <Flex gap={12}>
          <Button size="small" use="secondary" onClick={openDeleteConfirm}>
            Delete Fob/Card
          </Button>
          <Button size="small" use="danger" onClick={() => setShowRevokeModal(true)}>
            Revoke Fob/Card
          </Button>
          <Button size="small" disabled={!hasChanges} onClick={() => setShowUpdateModal(true)}>
            Update Fob/Card
          </Button>
        </Flex>
      </Flex>
      {showUpdateModal && (
        <SaltoSpaceIssueFobModal
          isOpen={showUpdateModal}
          buildingId={buildingId}
          headerTitle="Update physical fob/card"
          prepareMessage={UPDATE_PREPARE_MESSAGE}
          successTitle="Fob/card successfully updated!"
          successBody="The resident can now use the updated access configuration."
          onProgram={handleUpdate}
          onClose={() => setShowUpdateModal(false)}
          onSuccess={() => toastFactory.success('FOB/Card updated successfully')}
        />
      )}
      {showRevokeModal && (
        <SaltoSpaceIssueFobModal
          isOpen={showRevokeModal}
          buildingId={buildingId}
          headerTitle="Revoke physical fob/card"
          prepareMessage={REVOKE_PREPARE_MESSAGE}
          successTitle="Fob/card successfully revoked!"
          successBody="The resident can no longer use it for access."
          onProgram={handleRevoke}
          onClose={() => setShowRevokeModal(false)}
          onSuccess={() => {
            setShowRevokeModal(false)
            toastFactory.success('FOB/Card revoked successfully')
          }}
        />
      )}
    </S.TabContent>
  )
}

interface SaltoSpaceExternalFobsProps {
  buildingId: number
  clientEmail: string
  enabled: boolean
  doorOptions: Option[]
  zoneOptions: Option[]
  accessLevelOptions: Option[]
}

export const SaltoSpaceExternalFobs = ({
  buildingId,
  clientEmail,
  enabled,
  doorOptions,
  zoneOptions,
  accessLevelOptions,
}: SaltoSpaceExternalFobsProps) => {
  const {
    data: fobs = [],
    isLoading,
    isError,
    refetch,
  } = useGetSaltoSpaceFobs(buildingId, clientEmail, enabled)

  if (!enabled) return null

  const externalFobs = fobs.filter((fob) => fob.fobMarker === 'external' && fob.fobId)

  const title = (
    <S.FobSectionTitle>
      <Typography size="text-medium" weight="medium" color="black800" as="span">
        External Salto Space physical Fob/Card
      </Typography>
      <Tooltip triggerIcon={InfoIcon}>{EXTERNAL_FOB_TOOLTIP}</Tooltip>
    </S.FobSectionTitle>
  )

  if (isLoading) {
    return (
      <S.FobSection>
        {title}
        <KeylessTabSkeleton rowCount={4} />
      </S.FobSection>
    )
  }

  if (isError) {
    return (
      <S.FobSection>
        {title}
        <Flex justify="space-between" align="center" gap={12} padding="12 16">
          <Typography size="text-small" color="black500" as="span">
            Couldn't load external fobs/cards.
          </Typography>
          <Button size="small" use="secondary" onClick={() => void refetch()}>
            Retry
          </Button>
        </Flex>
      </S.FobSection>
    )
  }

  if (externalFobs.length === 0) return null

  return (
    <S.FobSection>
      {title}
      {externalFobs.map((fob) => (
        <SaltoFobRecord
          key={fob.fobHolderId}
          fob={fob}
          buildingId={buildingId}
          clientEmail={clientEmail}
          doorOptions={doorOptions}
          zoneOptions={zoneOptions}
          accessLevelOptions={accessLevelOptions}
        />
      ))}
    </S.FobSection>
  )
}
